import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Plus, Search, Trash2, Edit2, Users, X, Phone, Mail, MapPin, Building2 } from 'lucide-react';

// Simple Modal Component
const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-chivo font-extrabold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Suppliers = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Supplier Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, [currentBranch]); // Reload when branch changes

  const fetchSuppliers = async () => {
    try {
      // Build branch filter params
      const branchParams = currentBranch 
        ? `?branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      const response = await api.get(`/suppliers${branchParams}`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        // Branch assignment - for new suppliers assign current branch (not for Matriz)
        branch_id: editingSupplier ? editingSupplier.branch_id : (isMatriz ? null : currentBranch?.branch_id)
      };
      
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.supplier_id}`, data);
        toast.success('Proveedor actualizado');
      } else {
        await api.post('/suppliers', data);
        toast.success('Proveedor creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setForm({
      name: supplier.name,
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Proveedor eliminado');
      fetchSuppliers();
    } catch (error) {
      toast.error('Error al eliminar proveedor');
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setForm({ name: '', phone: '', email: '', address: '', notes: '' });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.phone?.includes(searchQuery)
  );

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold" data-testid="suppliers-title">
                Proveedores
              </h1>
              <p className="text-muted-foreground">{suppliers.length} proveedores registrados</p>
            </div>
            
            <Button 
              className="bg-accent text-white hover:bg-accent/90"
              onClick={openNewDialog}
              data-testid="add-supplier-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proveedor
            </Button>
          </div>

          {/* Search */}
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Suppliers Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSuppliers.map((supplier, idx) => (
              <motion.div
                key={supplier.supplier_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="border border-border/50 hover:shadow-lg transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-lg">{supplier.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(supplier)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(supplier.supplier_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {supplier.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-accent" />
                          <a href={`tel:${supplier.phone}`} className="hover:underline">
                            {supplier.phone}
                          </a>
                        </div>
                      )}
                      {supplier.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <a href={`mailto:${supplier.email}`} className="hover:underline truncate">
                            {supplier.email}
                          </a>
                        </div>
                      )}
                      {supplier.address && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{supplier.address}</span>
                        </div>
                      )}
                      {supplier.notes && (
                        <p className="text-sm text-muted-foreground mt-2 pt-2 border-t border-border/50">
                          {supplier.notes}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredSuppliers.length === 0 && !loading && (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No hay proveedores registrados</p>
                <Button 
                  className="bg-accent text-white hover:bg-accent/90"
                  onClick={openNewDialog}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Proveedor
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Supplier Modal */}
      <Modal
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        title={editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre del Proveedor *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Distribuidora del Mar"
              required
              data-testid="supplier-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Teléfono / Celular</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="55 1234 5678"
                className="pl-10"
                data-testid="supplier-phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Correo Electrónico</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="proveedor@email.com"
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Dirección</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Calle, número, colonia..."
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notas adicionales..."
            />
          </div>

          <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90">
            {editingSupplier ? 'Actualizar' : 'Crear'} Proveedor
          </Button>
        </form>
      </Modal>
    </div>
  );
};

export default Suppliers;
